import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { UrlParamKeys } from '../../../../app/UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '../../../../app/utils/ConnectUtils';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '../../../../ts/models/sort/SortOptionDefinitions';
import { IOrderClause } from '../../../../ts/ServerApi/types/IOrderClause';
import { resetIgnoreNextOrderUrlChange, setOrderAction } from '../../../actions/SetOrderAction';
import { sortContent } from '../../../actions/SortContentAction';
import { ExplorableItemType } from '../../../models/ContentType';
import { selectCurrentlyApplicableContentTypes } from '../../../selectors/ContentFilterSelectors';
import { selectContentSortIgnoreNextUrlChange } from '../../../selectors/ContentSortSelectors';
import { selectSpaceInfo } from '../../../selectors/SpaceInfoSelectors';
import { SpaceInfo } from '../../../state/SpaceInfo';
import { getOrderClausesFromQueryParam } from '../ExplorerUrlBindings';

export interface SortOrderUpdaterOwnProps {
  readonly routeLocationSearch: string;
  readonly routeLocationPath: string;
  readonly resetStore?: boolean;
}

export interface SortOrderUpdaterStateProps {
  readonly contentTypes: ReadonlyArray<ExplorableItemType>;
  readonly spaceInfo: SpaceInfo;
  readonly ignoreNextUrlChange: boolean;
}

export interface SortOrderUpdaterDispatchProps {
  readonly setOrderAction: (order: IOrderClause[]) => void;
  readonly sortContent: (order: IOrderClause[]) => void;
  readonly resetIgnoreNextOrderUrlChange: () => void;
}

export type SortOrderUpdaterProps = SortOrderUpdaterOwnProps
  & SortOrderUpdaterStateProps
  & SortOrderUpdaterDispatchProps;

/**
 * Component that syncs the sort order from the URL query params to the redux store before rendering its children.
 */
export class SortOrderUpdater extends React.Component<SortOrderUpdaterProps> {
  constructor(props: SortOrderUpdaterProps) {
    super(props);
    this.props.setOrderAction(this.getOrderFromQuery());
  }

  render() {
    return <>{this.props.children}</>;
  }

  componentDidUpdate(prevProps: SortOrderUpdaterProps) {
    // If resetStore is set, dispatch the setOrder action and nothing else
    // TFSID:983648.  Handle case when updating this component with a new projectId
    if (this.props.resetStore) {
      this.props.setOrderAction(this.getOrderFromQuery());
    } else {
      const queryParams = queryString.parse(this.props.routeLocationSearch);
      const prevQueryParams = queryString.parse(prevProps.routeLocationSearch);

      // Don't do anything if the order query param has not changed
      if (prevQueryParams[UrlParamKeys.Order] === queryParams[UrlParamKeys.Order]) return;

      if (prevProps.routeLocationPath === this.props.routeLocationPath && !this.props.ignoreNextUrlChange) {
        this.props.sortContent(this.getOrderFromQuery());
      } else if (prevProps.routeLocationPath === this.props.routeLocationPath && this.props.ignoreNextUrlChange) {
        this.props.resetIgnoreNextOrderUrlChange();
      }
    }
  }

  private getOrderFromQuery() {
    const queryParams = queryString.parse(this.props.routeLocationSearch);
    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(this.props.contentTypes, this.props.spaceInfo);
    return getOrderClausesFromQueryParam(queryParams[UrlParamKeys.Order], defaultOrder);
  }
}

const mapStateToProps: MapStateToProps<SortOrderUpdaterStateProps> = state => ({
  contentTypes: selectCurrentlyApplicableContentTypes(state),
  spaceInfo: selectSpaceInfo(state),
  ignoreNextUrlChange: selectContentSortIgnoreNextUrlChange(state)
});

const mapDispatchToProps: MapDispatchToProps<SortOrderUpdaterDispatchProps> = dispatch => bindActionCreators({
  setOrderAction,
  sortContent,
  resetIgnoreNextOrderUrlChange
}, dispatch);

export const ConnectedSortOrderUpdater = connect(mapStateToProps, mapDispatchToProps)(SortOrderUpdater);

/**
 * Creates a higher order component that wraps the given component with the SortOrderUpdate component.
 */
export function withSortOrderUpdater<P>(Component: React.ComponentType<P>) {
  return (props: P & SortOrderUpdaterProps) => (
    <ConnectedSortOrderUpdater {...props}><Component {...props} /></ConnectedSortOrderUpdater>
  );
}
